import React, { useState } from 'react';
import { tutorialData } from 'enums';
import { ChevronDown, ExternalLink, Play } from 'lucide-react';

const TutorialPage = () => {
  // First tutorial is open by default (index 0)
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-black text-base">
            Learn how to get the most out of NirvaScribe with our step-by-step video guides
          </p>
        </div>

        {/* Tutorial Sections */}
        <div className="space-y-4">
          {tutorialData.map((section, sectionIndex) => {
            const globalIndex = tutorialData
              .slice(0, sectionIndex)
              .reduce((acc, s) => acc + s.tutorials.length, 0);

            return section.tutorials.map((tutorial, tutorialIndex) => {
              const currentIndex = globalIndex + tutorialIndex;
              const isOpen = openIndex === currentIndex;

              return (
                <div
                  key={currentIndex}
                  className={`bg-white rounded-2xl shadow-md transition-all duration-300 overflow-hidden ${
                    isOpen ? 'ring-2 ring-primary' : 'hover:shadow-lg'
                  }`}
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => handleToggle(currentIndex)}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div
                        className={`p-3 rounded-xl transition-colors ${
                          isOpen ? 'bg-primary' : 'bg-gray-100'
                        }`}
                      >
                        <Play
                          className={`w-5 h-5 ${
                            isOpen ? 'text-black' : 'text-gray-600'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-bold text-black mb-1">
                          {section.sectionTitle}
                        </div>
                        <h3 className="text-sm font-bold text-sky-400">
                          {tutorial.title}
                        </h3>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-6 h-6 text-black transition-transform duration-300 flex-shrink-0 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Accordion Content */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      isOpen
                        ? 'max-h-[800px] opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pt-2">
                      {/* Description */}
                      <p className="text-black mb-6 leading-relaxed">
                        {tutorial.description}
                      </p>

                      {/* Video Player */}
                      <div className="relative rounded-xl overflow-hidden bg-black shadow-xl">
                        <video
                          controls
                          className="w-full h-auto"
                          preload="metadata"
                        >
                          <source src={tutorial.videoUrl} type="video/mp4" />
                          <source src={tutorial.videoUrl} type="video/quicktime" />
                          Your browser does not support the video tag.
                        </video>
                      </div>

                      {/* Video Info + Resource Link */}
                      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                        <div className="px-3 py-1 bg-gray-100 rounded-full text-sm text-black">
                          Video Tutorial
                        </div>
                        {tutorial.resourceLink && (
                          <a
                            href={tutorial.resourceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-black bg-primary border border-gray-300 rounded-lg hover:bg-[#96dfe3] hover:shadow-sm transition-all duration-200"
                          >
                            Click here for details
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })}
        </div>

        {/* Help Footer */}
        <div className="mt-12 p-6 bg-white rounded-2xl shadow-md border border-gray-200">
          <h3 className="text-base font-semibold text-black mb-2">
            Need more help?
          </h3>
          <p className="text-black mb-4">
            Can't find what you're looking for? Our support team is here to help you get started.
          </p>
          <a 
            href="https://www.nirvascribe.com/contact-us" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-2 bg-primary text-black rounded-lg font-medium hover:bg-opacity-90 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;